import isEmpty from 'lodash-es/isEmpty'

import type { OnboardingProps } from '@components/common/Onboarding'
import { useDashboardFeatureFlags } from '@components/utils/DashboardFeatureFlags'
import { ConfirmModal, useModalContext } from '@fourthwall/components'
import type { SchedulerProps } from '@modules/Dashboard/Home/components/Scheduler'
import { checklistDataLayer } from '@utils/gtm/checklistDataLayer'
import { routing } from '@utils/routing'

import { useMembershipsOnboarding } from '../../../Memberships/modules/Overview/modules/Onboarding/hooks/useMembershipsOnboarding'
import { useCurrentShopQuery } from '../../../hooks/useCurrentShopQuery'
import { useOnboardingSummaryQuery } from '../../queries/useOnboardingSummaryQuery'
import { useChecklistActionVisibilityMutation } from '../useChecklistActionVisibilityMutation'
import { useChecklistVisibilityMutation } from '../useChecklistVisibilityMutation'
import { useScheduler } from '../useScheduler'
import type { ChecklistItem, OnboardingSummaryChecklistItemType, OnboardingType } from './types'
import { getShopChecklistItemsConfig, sortChecklistItems } from './utils'

export const useOnboarding = () => {
  const { open } = useModalContext()
  const { dashboardFeatureFlags } = useDashboardFeatureFlags()
  const membershipsOnboarding = useMembershipsOnboarding({
    page: 'Home',
    title: 'Memberships setup',
  })
  const { scheduler } = useScheduler()
  const { currentShopQuery } = useCurrentShopQuery()
  const { onboardingSummaryQuery, invalidateOnboardingSummaryQuery } = useOnboardingSummaryQuery()
  const { checklistVisibilityMutation } = useChecklistVisibilityMutation({
    onSuccess: () => invalidateOnboardingSummaryQuery(),
  })
  const { checklistActionVisibilityMutation } = useChecklistActionVisibilityMutation({
    onSuccess: () => invalidateOnboardingSummaryQuery(),
  })

  const skipAllShopItems = async () => {
    return await checklistVisibilityMutation.mutateAsync([{ newStatus: 'Hidden' }])
  }

  const skipItem = async (actionType: OnboardingSummaryChecklistItemType) => {
    await checklistActionVisibilityMutation.mutateAsync([{ actionType }, { newStatus: 'Hidden' }])
  }

  const shouldOnboarding =
    dashboardFeatureFlags.themeEditorOnboarding.enabled &&
    !currentShopQuery.data?.metafields['theme-editor-visited'] &&
    !currentShopQuery.data?.metafields['created-by-agency']

  const shopChecklistItemsConfig = getShopChecklistItemsConfig({
    openModal: open,
    skipItem,
    shouldOnboarding,
    themeEditorPath: routing.themeEditorV2.self,
  })

  const shopChecklistItems = ((): ChecklistItem[] => {
    if (!onboardingSummaryQuery.isSuccess) return []

    return onboardingSummaryQuery.data.checklist
      .sort(sortChecklistItems)
      .map<ChecklistItem>((item) => {
        if (!(item.action.type in shopChecklistItemsConfig)) {
          return {} as ChecklistItem
        }

        return {
          key: item.action.type,
          title: shopChecklistItemsConfig[item.action.type].title,
          description: shopChecklistItemsConfig[item.action.type].description,
          imageUrl: shopChecklistItemsConfig[item.action.type].imageUrl,
          primaryButton: shopChecklistItemsConfig[item.action.type].primaryButton,
          secondaryButton: shopChecklistItemsConfig[item.action.type].secondaryButton,
          completedButton: shopChecklistItemsConfig[item.action.type].completedButton,
          completed: item.status === 'Hidden' || item.action.status === 'Completed',
          node: shopChecklistItemsConfig[item.action.type].node,
        } as ChecklistItem
      })
      .filter((item) => !isEmpty(item))
  })()

  const allShopChecklistItemsCompleted = !shopChecklistItems.some(({ completed }) => !completed)

  const onboardingType = ((): OnboardingType => {
    if (!dashboardFeatureFlags.homeOnboarding.enabled) return 'NONE'

    if (
      !onboardingSummaryQuery.isSuccess ||
      membershipsOnboarding.isLoading ||
      !currentShopQuery.isSuccess
    ) {
      return 'LOADING'
    }

    const wasEverActive = !!currentShopQuery.data.lastLiveAt

    if (membershipsOnboarding.active) return 'ONBOARDING_SHOP_MEMBERSHIPS'

    if (!allShopChecklistItemsCompleted) return 'ONBOARDING_SHOP'

    // Don't display scheduler when shop was ever launched
    if (scheduler.schedulerProps && !wasEverActive) return 'SCHEDULER'

    return 'NONE'
  })()

  const checklistItems = (() => {
    if (onboardingType === 'ONBOARDING_SHOP_MEMBERSHIPS') {
      return [...shopChecklistItems, ...membershipsOnboarding.items]
    }

    if (onboardingType === 'ONBOARDING_SHOP') {
      return shopChecklistItems
    }

    if (onboardingType === 'ONBOARDING_MEMBERSHIPS') {
      return membershipsOnboarding.items
    }

    return []
  })()

  const completedItemsCount = checklistItems.filter(({ completed }) => completed).length
  const percentage = Math.floor((completedItemsCount / checklistItems.length) * 100)

  const title: string = (() => {
    return `${percentage}% complete`
  })()

  const getSkipLabel = () => {
    if (onboardingType === 'ONBOARDING_SHOP_MEMBERSHIPS' || onboardingType === 'ONBOARDING_SHOP') {
      return 'Skip all'
    }

    return ''
  }

  const onSkip = () => {
    if (onboardingType === 'ONBOARDING_SHOP_MEMBERSHIPS' || onboardingType === 'ONBOARDING_SHOP') {
      open(ConfirmModal, {
        title: 'Are you sure you want to skip launch checklist?',
        text: 'This action cannot be reversed.',
        confirmLabel: 'Skip',
        confirmButtonAnalytics: 'skipChecklistConfirm',
        onConfirm: () => {
          if (onboardingType === 'ONBOARDING_SHOP_MEMBERSHIPS') {
            membershipsOnboarding.onSkipConfirm()
          }
          skipAllShopItems()
          checklistDataLayer('skipChecklistConfirm', 'Home', 'Launch')
        },
      })
      if (onboardingType === 'ONBOARDING_SHOP_MEMBERSHIPS') {
        checklistDataLayer('skipChecklist', 'Home', 'Memberships')
      }
      checklistDataLayer('skipChecklist', 'Home', 'Launch')
    }

    if (onboardingType === 'ONBOARDING_MEMBERSHIPS') {
      membershipsOnboarding.onSkip()
    }
  }

  const onboardingProps: OnboardingProps = {
    progress: { title, percentage },
    onboardingListProps: { items: checklistItems },
    skipLabel: getSkipLabel(),
    onSkip,
  }

  const getOnboardingProps = (): OnboardingProps | undefined => {
    if (
      onboardingType === 'ONBOARDING_SHOP' ||
      onboardingType === 'ONBOARDING_MEMBERSHIPS' ||
      onboardingType === 'ONBOARDING_SHOP_MEMBERSHIPS'
    ) {
      return onboardingProps
    }
  }

  const schedulerProps: SchedulerProps | undefined = ((): SchedulerProps | undefined => {
    if (onboardingType === 'SCHEDULER' || onboardingType === 'SCHEDULER_MEMBERSHIPS') {
      return scheduler.schedulerProps
    }
  })()

  const onboarding = {
    onboardingType,
    schedulerProps,
    onboardingProps: getOnboardingProps(),
  }

  return { onboarding }
}
